import Box from "@/ui/atoms/Box";

import type { RowNode } from "@/ui/molecules/Grid";
import type { CustomCellRendererProps } from "@/ui/molecules/Grid";
import { Badge } from "@proda-ai/ui-kit";

import type {
    DealExtractionInfo,
    DealInfoWithParent,
    DealExtractionInfoWithParent,
} from "../dealTypes";
import { isNullOrUndefined } from "@/utils/guards";

type ExtendedCellRendererProps = CustomCellRendererProps<DealExtractionInfo> & {
    onViewDeal: (params: { projectId: number; databaseId: number }) => void;
    getDealUrl: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => string;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    onViewExtraction: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    onViewRentRoll?: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) => void;
    databaseId: number;
};

export const testIds = {
    menuButton: "DealNameCellRenderer-menuButton",
};

export const DealNameCellRenderer: React.FC<ExtendedCellRendererProps> = ({
    onViewDeal,
    onViewExtraction,
    onViewRentRoll,
    getDealUrl,
    getExtractionUrl,
    getRentRollExtractionUrl,
    api,
    column,
    node,
    value,
}) => {
    const projectId = (node.data as DealInfoWithParent).projectId;
    const databaseId = (node.data as DealInfoWithParent).databaseId;

    const getUrl = (data: DealExtractionInfoWithParent) => {
        if (data.extractionType !== "rent_roll_extraction") {
            return getExtractionUrl({
                projectId: data.projectId,
                databaseId: data.databaseId,
                extractionId: data.extractionId,
                extractionStatus: data.status as "draft" | "completed",
            });
        } else {
            if (
                data.excelsiorFileId === null ||
                data.excelsiorFileId === undefined
            ) {
                return "#";
            }
            return getRentRollExtractionUrl({
                fileId: data.excelsiorFileId,
                extractionId: Math.abs(data.extractionId),
                sheetId: `s${data.sheetId}`,
                databaseId: data.databaseId,
            });
        }
    };

    const dealOrExtractionUrl =
        node.level === 0
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              getDealUrl({ projectId: projectId!, databaseId })
            : node.data
              ? getUrl(node.data as DealExtractionInfoWithParent)
              : "#";

    const onMenuClick = (event: React.MouseEvent) => {
        event.preventDefault();
        api.showContextMenu({
            value: node.data,
            x: event.clientX,
            y: event.clientY,
            column: column,
            rowNode: node as RowNode,
        });
    };

    const handleCellClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const rentRollRow =
            node.level === 1 &&
            node.data &&
            (node.data as DealExtractionInfoWithParent).extractionType ===
                "rent_roll_extraction"
                ? (node.data as DealExtractionInfoWithParent)
                : null;
        if (rentRollRow) {
            // Rows with a file id navigate via the native href; overview rows
            // have none, so resolve their location on click instead.
            if (isNullOrUndefined(rentRollRow.excelsiorFileId)) {
                e.preventDefault();
                if (!isNullOrUndefined(rentRollRow.projectId)) {
                    onViewRentRoll?.({
                        projectId: rentRollRow.projectId,
                        databaseId: rentRollRow.databaseId,
                        extractionId: rentRollRow.extractionId,
                    });
                }
            }
            return;
        }
        e.preventDefault();
        const projectId = (node.data as DealInfoWithParent).projectId;
        const databaseId = (node.data as DealInfoWithParent).databaseId;
        if (isNullOrUndefined(projectId)) {
            return;
        }
        if (node.level === 0) {
            onViewDeal({
                projectId: projectId,
                databaseId: databaseId,
            });
        } else if (node.level === 1) {
            const extractionType = (node.data as DealExtractionInfoWithParent)
                .extractionType;
            if (extractionType === "rent_roll_extraction") {
                return;
            }
            onViewExtraction({
                projectId: projectId,
                databaseId: databaseId,
                extractionId: (node.data as DealExtractionInfoWithParent)
                    .extractionId,
                extractionStatus: (node.data as DealExtractionInfoWithParent)
                    .status as "draft" | "completed",
            });
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
            }}
        >
            <Box
                sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                }}
                style={{ cursor: "pointer" }}
            >
                <a
                    href={dealOrExtractionUrl}
                    onClick={handleCellClick}
                    data-testid={`DealNameCell-${projectId}`}
                    data-projectid={projectId}
                >
                    {value}
                </a>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                }}
            >
                {Number(
                    (node.data as DealInfoWithParent | undefined)
                        ?.numOfExtractions,
                ) >= 0 ? (
                    <Badge
                        label={String(
                            (node.data as DealInfoWithParent).numOfExtractions,
                        )}
                        pattern="secondary"
                    />
                ) : null}
                <Box
                    data-testid={testIds.menuButton}
                    onClick={onMenuClick}
                    sx={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                    }}
                >
                    ⋮
                </Box>
            </Box>
        </Box>
    );
};
